import React, { Component } from 'react';
import isNumber from 'lodash/isNumber';
import isEmpty from 'lodash/isEmpty';
import MarketingBanner from './MarketingBanner.component';
import MarketingModal from './MarketingModal.component';
import {
	CampaignButtonType,
	CampaignType,
	CampaignButtonPosition,
	ActionIdMap,
} from './MarketingCampaign.enum';
import Utils from '../utils';

const { Communication, Logger, Navigation, Session, Util, withLayoutAndModalContexts } = Utils;

class MarketingCampaign extends Component {
	constructor(props) {
		super(props);

		this.state = {
			campaign: {},
			groups: [],
			showBanner: false,
			modalId: null,
		};
	}

	async componentDidMount() {
		const {
			layoutContext: { isMobile },
		} = this.props;
		const { settings, isSignedIn } = Session.getData();
		const { Page = '' } = settings;
		const params = { appType: Page };

		// do not call getMarketingCampaignToShow if user is on mobile, or is not authenticated
		if (isMobile || !isSignedIn) {
			return;
		}

		const response = await Communication.getMarketingCampaignToShow(params);

		if (!Communication.isSuccessful(response)) {
			return Logger.logToServer({
				category: 'marketing_campaign',
				'Error Description': response.ErrorDescription,
				action: 'load_marketing_view',
			});
		}

		const { campaign = {}, groups = [] } = response.data;
		const campaignObj = campaign[0] || {};

		if (!isEmpty(campaignObj)) {
			this.setState({ campaign: campaignObj, groups });
			this.renderCampaignBasedOnType(campaignObj);
		}
	}

	// show campaign based on CampaignTypeId - 1: Banner, 2: Modal
	renderCampaignBasedOnType = campaign => {
		const {
			modalContext: { showModal },
		} = this.props;
		const { CampaignTypeId } = campaign;

		if (CampaignTypeId === CampaignType.Banner) {
			this.setState({
				showBanner: true,
			});
		} else if (CampaignTypeId === CampaignType.Modal) {
			const modalId = showModal({
				component: MarketingModal,
				data: {
					campaign,
					triggerCampaignAction: this.triggerCampaignAction,
				},
			});

			this.setState({
				modalId,
			});
		}

		Logger.saveActionData('Marketing Campaign Shown');
		this.logEvent('view_campaign');
	};

	hideCampaign = () => {
		const {
			campaign: { CampaignTypeId },
			modalId,
		} = this.state;
		const {
			modalContext: { closeModal },
		} = this.props;

		if (CampaignTypeId === CampaignType.Modal) {
			closeModal(modalId);
		} else if (CampaignTypeId === CampaignType.Banner) {
			this.setState({
				showBanner: false,
			});
		}

		Logger.saveActionData('Marketing Campaign Closed');
	};

	// trigger a specific action based on input
	triggerCampaignAction = (buttonPosition = 0) => {
		const {
			campaign: { CampaignTypeId },
		} = this.state;

		const buttonType = this.getCampaignButtonTypeByPosition(buttonPosition);
		const buttonUrl = this.getCampaignButtonUrlByPosition(buttonPosition);
		const buttonActionId = this.getCampaignButtonActionIdByPosition(buttonPosition);

		// / if buttonPosition is 0, the Marketing Modal has been closed
		if (buttonPosition === 0) {
			this.hideCampaign();
		}

		// system closes the campaign banner or modal
		if (buttonType === CampaignButtonType.Close) {
			this.hideCampaign();

			// system opens a new tab with the url
		} else if (buttonType === CampaignButtonType.Link) {
			Navigation.newTab(buttonUrl);

			// trigger action item
		} else if (buttonType === CampaignButtonType.Action) {
			// close the campaign dialog view, but not the banner view
			if (CampaignTypeId === CampaignType.Modal) {
				this.hideCampaign();
			}

			if (isNumber(buttonActionId) && buttonActionId > 0) {
				this.handleAction(buttonActionId);
			}
		}

		// notify the server with the marketing campaign activity
		this.recordMarketingCampaignActivity(buttonPosition);

		Logger.saveActionData('Marketing Campaign Button Clicked');
	};

	handleAction = actionId => {
		const { configuredCarriers = [] } = Session.getAccountInfo();
		const { action, params } = ActionIdMap[actionId];

		switch (action) {
			case 'Upgrade':
				// TODO: Implement Shipstation Upgrade flow
				Util.setCookie('upgradeTrigger', 'myaccount_upgrade_trigger');
				break;
			case 'CampaignSettings':
				// TODO: open 'Settings' dialog, with @params tab in @settings group selected
				break;
			case 'Link':
				/** TODO:
				 * Redirect to the marketing page (/cm) if user is already registered in Shipstation
				 * Otherwise, begin Shipstation reg flow. On success, redirect to marketing page.
				 */
				Navigation.navigateTo('marketing');
				break;
			case 'AddCarrier':
				/** TODO:
				 * If 'UPS' is already configured as a carrier, redirect to Carrier tab in Common settings
				 * Otherwise, begin Add UPS Account flow.
				 */
				if (configuredCarriers.includes(params)) {
					// If designated carrier is already configured, trigger action to display carrier settings
					this.handleAction(13);
				} else {
					// trigger Add UPS Account flow
				}
				break;
			default:
				break;
		}
	};

	recordMarketingCampaignActivity = async buttonPosition => {
		const {
			campaign: { CampaignId },
		} = this.state;

		const action = this.getUserActionByPosition(buttonPosition);
		const activityType = this.getUserActivityTypeByPosition(buttonPosition);
		const { Page = '' } = Session.getSettings();
		const params = {
			campaignId: CampaignId,
			AppType: Page,
			ActivityType: activityType,
			ButtonPosition: buttonPosition,
		};

		if (isNumber(CampaignId) && CampaignId > 0) {
			const response = await Communication.recordMarketingCampaignActivity(params);

			if (!Communication.isSuccessful(response)) {
				Logger.logToServer({
					category: 'marketing_campaign',
					campaignId: CampaignId,
					'Error Description': response,
					action,
				});
			}

			this.logEvent(action);
		}
	};

	getCampaignButtonTypeByPosition = buttonPosition => {
		const {
			campaign: { PrimaryButtonTypeId, SecondaryButtonTypeId },
		} = this.state;

		if (buttonPosition === CampaignButtonPosition.Primary) {
			return PrimaryButtonTypeId;
		} else if (buttonPosition === CampaignButtonPosition.Secondary) {
			return SecondaryButtonTypeId;
		}
	};

	getCampaignButtonUrlByPosition = buttonPosition => {
		const {
			campaign: { PrimaryButtonUrl, SecondaryButtonUrl },
		} = this.state;

		if (buttonPosition === CampaignButtonPosition.Primary) {
			return PrimaryButtonUrl;
		} else if (buttonPosition === CampaignButtonPosition.Secondary) {
			return SecondaryButtonUrl;
		}
	};

	getCampaignButtonActionIdByPosition = buttonPosition => {
		const {
			campaign: { PrimaryButtonActionId, SecondaryButtonActionId },
		} = this.state;

		if (buttonPosition === CampaignButtonPosition.Primary) {
			return PrimaryButtonActionId;
		} else if (buttonPosition === CampaignButtonPosition.Secondary) {
			return SecondaryButtonActionId;
		}
	};

	getUserActionByPosition = buttonPosition => {
		if (buttonPosition === CampaignButtonPosition.Primary) {
			return 'click_primary_button';
		} else if (buttonPosition === CampaignButtonPosition.Secondary) {
			return 'click_secondary_button';
		}

		return 'click_campaign_close';
	};

	getUserActivityTypeByPosition = buttonPosition => {
		if (
			buttonPosition === CampaignButtonPosition.Primary ||
			buttonPosition === CampaignButtonPosition.Secondary
		) {
			return 'Clicked';
		}

		return 'Closed';
	};

	// dynamically send splunk log based on action input
	logEvent = action => {
		const {
			campaign: { CampaignId },
			groups,
		} = this.state;

		if (isNumber(CampaignId) && CampaignId > 0) {
			Logger.logToServer({
				category: 'marketing_campaign',
				campaignId: CampaignId,
				groupID: this.buildGroupIds(groups),
				action,
			});
		}
	};

	// build a stringified array of groupIDs to log
	buildGroupIds = groupsArray => {
		let groupIds = '';

		if (groupsArray) {
			groupsArray.forEach(group => {
				if (groupIds.length > 0) {
					groupIds += ', ';
				}

				groupIds += `groupID="${group.GroupId}"`;
			});
		}

		return `[${groupIds}]`;
	};

	render() {
		const { campaign, showBanner } = this.state;

		if (showBanner) {
			return (
				<MarketingBanner
					campaign={campaign}
					triggerCampaignAction={this.triggerCampaignAction}
				/>
			);
		}

		return null;
	}
}

export default withLayoutAndModalContexts(MarketingCampaign);
